import { useMemo } from 'react';
import { useUserContext } from '../../context/UserContext';
import { calculateStudyParticipantDates } from '../../common/calculateStudyParticipantDates';
import { StudyParticipant } from '../../models/StudyParticipant';
import { StudyStatus } from '../../models/Study';
import { ParticipantEventOccurrence } from '../../screens/study/helpers/ParticipantEventHelpers';

export function useHasActiveStudy() {
  const { studyParticipants, allOccurrences } = useUserContext();

  return useMemo(
    () => hasActiveStudy(studyParticipants ?? [], allOccurrences),
    [allOccurrences, studyParticipants]
  );
}

// Eligible = not completed, paused, or disqualified.
export const isEligibleStudyParticipant = (
  studyParticipant: StudyParticipant
) => {
  const dates = calculateStudyParticipantDates(
    studyParticipant.study,
    studyParticipant
  );
  return (
    !dates.isCompletedStudy &&
    !studyParticipant.paused &&
    !studyParticipant.disqualified
  );
};

// A study can only be started while Testing or Active, Recruiting.
export const isJoinableStudyParticipant = (
  studyParticipant?: StudyParticipant
) =>
  studyParticipant?.study?.status === StudyStatus.Testing ||
  studyParticipant?.study?.status === StudyStatus.ActiveRecruiting;

const lastCompletedActivityDate = (studyParticipant: StudyParticipant) => {
  let latest: Date | undefined;
  for (const participantEvent of studyParticipant.participantEvents || []) {
    for (const log of participantEvent.participantEventLogs || []) {
      if (log.completedDate && (!latest || log.completedDate > latest)) {
        latest = log.completedDate;
      }
    }
  }
  return latest;
};

// The participant a caregiver worked on last, or undefined when none of them
// has a completed form. Ties keep the incoming list order.
const selectMostRecentlyActiveParticipant = (
  studyParticipants: StudyParticipant[]
) => {
  let selected: StudyParticipant | undefined;
  let selectedDate: Date | undefined;
  for (const studyParticipant of studyParticipants) {
    const completedDate = lastCompletedActivityDate(studyParticipant);
    if (completedDate && (!selectedDate || completedDate > selectedDate)) {
      selected = studyParticipant;
      selectedDate = completedDate;
    }
  }
  return selected;
};

// Single prioritized pick shared across the app so selections can't diverge:
// the study with the most recently completed form when a caregiver manages
// several — then a joinable one, then any eligible study.
export const selectPrioritizedStudyParticipant = (
  studyParticipants: StudyParticipant[]
) => {
  const eligible = (studyParticipants || []).filter(isEligibleStudyParticipant);
  return (
    selectMostRecentlyActiveParticipant(eligible) ??
    eligible.find(sp => !!sp.startDate) ??
    eligible.find(isJoinableStudyParticipant) ??
    eligible[0]
  );
};

export const gActiveStudyParticipant = (
  studyParticipants: StudyParticipant[],
  allOccurrences: ParticipantEventOccurrence[]
) => {
  const studyParticipant = selectPrioritizedStudyParticipant(studyParticipants);
  if (allOccurrences.length > 0) {
    return studyParticipant;
  } else {
    return undefined;
  }
};

export const hasActiveStudy = (
  studyParticipants: StudyParticipant[],
  allOccurrences: ParticipantEventOccurrence[]
) => {
  const studyParticipant = gActiveStudyParticipant(
    studyParticipants,
    allOccurrences
  );
  return studyParticipant && allOccurrences.length > 0;
};
